import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import ShareBar from '@/components/feature/ShareBar';
import EmptyState from '@/components/base/EmptyState';
import WorkCard from '@/components/feature/WorkCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getWorkById, getRelatedWorks } from '@/lib/content';

export default function WorkDetailPage() {
  const { id } = useParams();
  const item = getWorkById(id);

  usePageMeta({
    title: item ? `${item.title} - 琉璃神社` : '作品不存在 - 琉璃神社',
    description: item
      ? `${item.intro.slice(0, 80)} 琉璃神社作品档案，包含类型、地区、上映时间、连载状态与相关作品推荐。`
      : '该作品档案不存在或已被删除，返回琉璃神社作品分类查看更多内容。',
    keywords: item ? `${item.title},${item.type},${item.tags.join(',')},动漫作品,琉璃神社` : '动漫作品,琉璃神社',
  });

  if (!item) {
    return (
      <SiteLayout>
        <div className="shell py-16 md:py-24">
          <Breadcrumb items={[{ label: '动漫作品', to: '/category/works' }, { label: '作品不存在' }]} />
          <div className="mt-8">
            <EmptyState
              icon="ri-error-warning-line"
              title="作品不存在或已删除"
              description="这个作品档案可能已经调整，或者链接地址有误。回到作品分类看看其他推荐吧。"
              actionLabel="返回作品列表"
              actionTo="/category/works"
            />
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedWorks(item.id, 3);
  const infoRows = [
    { label: '作品类型', value: item.type },
    { label: '地区', value: item.region },
    { label: '上映 / 连载时间', value: `${item.year} 年` },
    { label: '连载状态', value: item.status },
    { label: '制作公司 / 出版', value: item.studio },
    { label: '主创', value: item.creator },
    { label: '话数 / 卷数', value: item.episodes },
    { label: '站内评分', value: `${item.score.toFixed(1)} / 10` },
  ];

  return (
    <SiteLayout>
      <div className="w-full bg-background-100 border-b border-background-200/70">
        <div className="w-full px-4 md:px-8 py-7 md:py-9">
          <Breadcrumb
            items={[
              { label: '动漫作品', to: '/category/works' },
              { label: item.type, to: '/category/works' },
              { label: '作品详情' },
            ]}
          />
        </div>
      </div>

      <div className="shell py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <div className="w-full max-w-[320px] lg:max-w-none aspect-[3/4] rounded-lg overflow-hidden border border-background-200/80 bg-background-200">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} - 琉璃神社作品介绍`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="mt-4 flex items-center justify-between rounded-lg border border-background-200/80 bg-background-50 px-4 py-3">
                <div>
                  <p className="text-[11px] text-secondary-500 font-label">站内评分</p>
                  <p className="mt-0.5 text-2xl font-label text-primary-700 leading-none">
                    {item.score.toFixed(1)}
                    <span className="text-xs text-secondary-400 ml-1">/ 10</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-secondary-500 font-label">热度</p>
                  <p className="mt-0.5 text-sm text-foreground-800 font-label">
                    {item.popularity.toLocaleString('en-US')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-1 rounded bg-primary-50 text-primary-700 text-[11px] whitespace-nowrap">
                {item.type}
              </span>
              <span className="px-2.5 py-1 rounded bg-secondary-100 text-secondary-700 text-[11px] whitespace-nowrap">
                {item.region}
              </span>
              <span className="px-2.5 py-1 rounded bg-accent-100 text-accent-800 text-[11px] whitespace-nowrap">
                {item.year}
              </span>
              {item.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-2.5 py-1 rounded bg-background-100 text-[11px] text-secondary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <h1 className="mt-4 text-2xl md:text-4xl font-semibold tracking-wide">{item.title}</h1>
            <p className="mt-2 text-sm text-secondary-500 font-label tracking-wide">{item.originalTitle}</p>

            <p className="mt-5 text-[15px] md:text-base text-foreground-700 leading-8">{item.intro}</p>

            <div className="mt-6">
              <ShareBar title={item.title} />
            </div>

            <div className="mt-8 rounded-lg border border-background-200/80 overflow-hidden">
              <div className="px-5 py-3.5 bg-background-100/80 border-b border-background-200/70">
                <h2 className="text-sm font-semibold">基本资料</h2>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2">
                {infoRows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-start gap-4 px-5 py-3.5 border-b border-background-200/60 last:border-0 sm:[&:nth-last-child(2)]:border-b-0"
                  >
                    <dt className="w-28 shrink-0 text-xs text-secondary-500">{row.label}</dt>
                    <dd className="text-xs md:text-sm text-foreground-800 leading-6">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-10">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-px bg-accent-500"></span>
                <h2 className="text-lg md:text-xl font-semibold">剧情简介与看点</h2>
              </div>
              {item.content.map((paragraph, index) => (
                <div key={paragraph.slice(0, 12)}>
                  <p className="text-[15px] md:text-base text-foreground-700 leading-[1.95] mb-5">
                    {paragraph}
                  </p>
                  {index === 1 && (
                    <div className="my-7 rounded-lg bg-primary-950 px-5 py-5 md:px-6">
                      <p className="font-heading text-base md:text-lg text-background-50 leading-8">
                        “{item.quote}”
                      </p>
                      <p className="mt-2 text-[11px] text-background-100/50 tracking-wide">
                        — 琉璃神社作品档案
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <Link
                to="/category/works"
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                返回作品档案
              </Link>
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/category/culture"
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-quill-pen-line w-4 h-4 flex items-center justify-center"></i>
                  相关文化专题
                </Link>
                <Link
                  to={`/search?q=${encodeURIComponent(item.title)}`}
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-search-line w-4 h-4 flex items-center justify-center"></i>
                  搜索相关资讯
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full bg-background-100/70 border-y border-background-200/70 py-10 md:py-14">
        <div className="shell">
          <div className="flex items-end justify-between gap-3 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-6 h-px bg-accent-500"></span>
                <span className="text-[11px] tracking-[0.24em] uppercase text-accent-600 font-label">
                  You May Also Like
                </span>
              </div>
              <h2 className="text-xl md:text-2xl font-semibold">相关作品推荐</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {related.map((rel) => (
              <WorkCard key={rel.id} item={rel} />
            ))}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}